const MAX_FILE_SIZE_BYTES = 2 * 1024 * 1024;
const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/jpg'];

const resolveFileType = (file) => {
  if (file?.type) return file.type;
  const name = String(file?.name || '').toLowerCase();
  if (name.endsWith('.png')) return 'image/png';
  if (name.endsWith('.jpg') || name.endsWith('.jpeg')) return 'image/jpeg';
  return '';
};

const resolveExtension = (fileType) => {
  if (fileType === 'image/png') return 'png';
  if (fileType === 'image/jpeg' || fileType === 'image/jpg') return 'jpg';
  return 'png';
};

export const formatBytes = (bytes) => {
  if (!bytes && bytes !== 0) return 'N/A';
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  const mb = kb / 1024;
  return `${mb.toFixed(2)} MB`;
};

export const validateQrFile = (file) => {
  const errors = [];
  if (!file) {
    errors.push('Please select a file.');
    return { isValid: false, errors };
  }

  const fileType = resolveFileType(file);
  if (!ALLOWED_TYPES.includes(fileType)) {
    errors.push('Only PNG or JPG files are allowed.');
  }

  if (file.size > MAX_FILE_SIZE_BYTES) {
    errors.push('File size must be 2MB or less.');
  }

  return { isValid: errors.length === 0, errors };
};

export const getImageDimensions = (file) =>
  new Promise((resolve, reject) => {
    const fileType = resolveFileType(file);
    if (!fileType) {
      reject(new Error('Unsupported file type.'));
      return;
    }
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const dimensions = { width: img.width, height: img.height };
      URL.revokeObjectURL(url);
      resolve(dimensions);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Unable to read image.'));
    };
    img.src = url;
  });

export const uploadStandeeQrToStorage = async ({ firebase, tenantId, uid, file }) => {
  if (!firebase?.storage) {
    throw new Error('Storage not available.');
  }
  const fileType = resolveFileType(file);
  const extension = resolveExtension(fileType);
  const storagePath = `tenants/${tenantId}/qrUploads/${uid}/standee-qr.${extension}`;
  const ref = firebase.storage.ref().child(storagePath);
  const uploadTask = await ref.put(file, {
    contentType: fileType,
    customMetadata: {
      tenantId: tenantId || '',
      userId: uid || '',
    },
  });
  const qrImageUrl = await uploadTask.ref.getDownloadURL();
  return { qrImageUrl, storagePath };
};

export const saveQrStandeePrefs = async (firebase, tenantId, uid, payload) => {
  if (!firebase?.db) {
    throw new Error('Firestore not available.');
  }
  const docRef = firebase.db
    .collection('franchises')
    .doc(tenantId)
    .collection('brandingPrefs')
    .doc(uid);

  await docRef.set(
    {
      tenantId,
      updatedAt: firebase.fieldValue.serverTimestamp(),
      updatedBy: uid,
      qrStandee: {
        qrImageUrl: payload.qrImageUrl || '',
        storagePath: payload.storagePath || '',
        fileName: payload.fileName || '',
        fileSize: payload.fileSize || 0,
        width: payload.width || 0,
        height: payload.height || 0,
        updatedAt: firebase.fieldValue.serverTimestamp(),
      },
    },
    { merge: true },
  );
};

export const loadQrStandeePrefs = async (firebase, tenantId, uid) => {
  if (!firebase?.db) {
    throw new Error('Firestore not available.');
  }
  const snapshot = await firebase.db
    .collection('franchises')
    .doc(tenantId)
    .collection('brandingPrefs')
    .doc(uid)
    .get();

  if (!snapshot.exists) return null;
  const data = snapshot.data() || {};
  return data.qrStandee || null;
};
